import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Swords, Loader2, Search, Coins, Check, X as XIcon, Crosshair } from "lucide-react";
import { sndBattleEntrance } from "@/pages/CardWars";

export default function Callouts({ lineup, savedLineup }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [target, setTarget] = useState(null);
  const [wager, setWager] = useState("");
  const [busy, setBusy] = useState(false);
  const debRef = useRef(null);
  const load = () => api.get("/cardwars/callouts").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const usingSaved = lineup.length !== 5 && savedLineup?.length === 5;
  const lineupToUse = lineup.length === 5 ? lineup : (usingSaved ? savedLineup : null);

  const search = (v) => {
    setQ(v); setTarget(null);
    clearTimeout(debRef.current);
    if (v.trim().length < 2) { setResults([]); return; }
    debRef.current = setTimeout(() => {
      api.get(`/members/search?q=${encodeURIComponent(v.trim())}`)
        .then(({ data }) => setResults((data || []).slice(0, 5))).catch(() => {});
    }, 250);
  };

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/cardwars/callouts", { opponent_id: target.id, copy_ids: lineupToUse, wager: Number(wager) || 0 });
      toast.success(`Callout sent — ${target.name} has 7 days to answer ⚔️`);
      setTarget(null); setQ(""); setWager(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const rematch = async (c) => {
    const dbl = Math.min(10000, (c.wager || 0) * 2);
    if (dbl > 0 && !window.confirm(`Run it back? The wager DOUBLES to ${dbl} BLC (escrowed until they answer).`)) return;
    setBusy(true);
    try {
      await api.post(`/cardwars/callouts/${c.id}/rematch`, { copy_ids: lineupToUse || undefined });
      toast.success(`Rematch sent — ${dbl > 0 ? `${dbl} BLC on the line` : "friendly run-back"} 🔁`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const respond = async (c, accept) => {
    if (accept && !lineupToUse) { toast.error("Pick 5 cards (or save a defense lineup) to answer"); return; }
    if (accept && c.wager > 0 && !window.confirm(`Answer ${c.challenger_name}'s callout? Your ${c.wager} BLC goes in — winner takes the pot.`)) return;
    setBusy(true);
    if (accept) sndBattleEntrance();
    try {
      const { data } = await api.post(`/cardwars/callouts/${c.id}/respond`, { accept, copy_ids: accept ? lineupToUse : undefined });
      if (!accept) toast.info("Callout declined — their wager was returned");
      else if (data.you_won) toast.success(`YOU WON ${data.result.s2}–${data.result.s1}${data.result.payout > 0 ? ` and took ${data.result.payout} BLC!` : "!"} 🏆`, { duration: 7000 });
      else toast.error(`${c.challenger_name} beat you ${data.result.s1}–${data.result.s2}. Run it back!`, { duration: 7000 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const { incoming, outgoing, recent } = data;
  return (
    <div className="border border-pb-border bg-pb-surface p-5 mb-8" data-testid="cw-callouts-panel">
      <div className="flex flex-wrap items-baseline gap-3 mb-3">
        <span className="font-display font-black uppercase text-lg flex items-center gap-2">
          <Crosshair className="w-5 h-5 text-pb-red" /> Direct Callouts
        </span>
        <span className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">Name your rival · name your wager · winner takes the pot</span>
      </div>

      {/* Send a callout */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input data-testid="callout-search-input" value={target ? target.name : q} onChange={(e) => search(e.target.value)}
            placeholder="Find a rival to call out…"
            className="w-full bg-pb-elevated border border-pb-border pl-8 pr-2 py-2.5 text-sm outline-none focus:border-pb-red" />
          {results.length > 0 && !target && (
            <div className="absolute top-full left-0 right-0 mt-1 z-20 bg-pb-surface border border-pb-border shadow-2xl" data-testid="callout-search-results">
              {results.map((r) => (
                <button key={r.id} data-testid={`callout-target-${r.id}`} onClick={() => { setTarget(r); setResults([]); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-pb-elevated flex items-center gap-2">
                  {r.avatar ? <img src={r.avatar} alt="" className="w-5 h-5 rounded-full object-cover" /> : <span className="w-5 h-5 rounded-full bg-pb-elevated" />}
                  {r.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <label className="flex items-center gap-1.5 border border-pb-border bg-pb-elevated px-2.5 shrink-0">
          <Coins className="w-3.5 h-3.5 text-yellow-400" />
          <input data-testid="callout-wager-input" type="number" min="0" step="1" value={wager} placeholder="0"
            onChange={(e) => setWager(e.target.value)}
            className="w-16 bg-transparent py-2.5 text-sm font-mono font-bold text-yellow-400 outline-none" />
          <span className="text-[9px] font-black text-zinc-500">BLC</span>
        </label>
        <button data-testid="callout-send-btn" onClick={send} disabled={busy || !target || !lineupToUse}
          className="flex items-center justify-center gap-1.5 bg-pb-red text-white text-[10px] font-black uppercase tracking-widest px-5 py-2.5 hover:bg-red-500 transition-colors disabled:opacity-40">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Swords className="w-3.5 h-3.5" />}
          Call Out{usingSaved ? " (defense lineup)" : ""}
        </button>
      </div>
      {!lineupToUse && <p className="text-[10px] text-zinc-600 -mt-2 mb-3">Pick 5 cards below (or save a defense lineup) to send or answer callouts.</p>}

      {/* Incoming */}
      {incoming.length > 0 && (
        <div className="mb-3" data-testid="callouts-incoming">
          <div className="text-[8px] font-black uppercase tracking-[0.3em] text-pb-red mb-1.5">You've been called out</div>
          {incoming.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-2 border border-pb-red/40 bg-red-950/20 px-3 py-2 mb-1.5" data-testid={`callout-in-${c.id}`}>
              <span className="text-xs"><b>{c.challenger_name}</b> challenges you{c.wager > 0 ? <> — <b className="text-yellow-400">{c.wager} BLC</b> each, winner takes all</> : " (friendly, no wager)"}</span>
              <span className="ml-auto flex gap-1.5">
                <button data-testid={`callout-accept-${c.id}`} onClick={() => respond(c, true)} disabled={busy}
                  className="flex items-center gap-1 bg-[#A3E635] text-black text-[9px] font-black uppercase tracking-widest px-3 py-1.5 hover:bg-lime-300 disabled:opacity-40">
                  <Check className="w-3 h-3" /> Accept
                </button>
                <button data-testid={`callout-decline-${c.id}`} onClick={() => respond(c, false)} disabled={busy}
                  className="flex items-center gap-1 border border-pb-border text-zinc-400 text-[9px] font-black uppercase tracking-widest px-3 py-1.5 hover:text-white disabled:opacity-40">
                  <XIcon className="w-3 h-3" /> Decline
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Outgoing */}
      {outgoing.length > 0 && (
        <div className="mb-3" data-testid="callouts-outgoing">
          <div className="text-[8px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-1.5">Waiting on an answer</div>
          {outgoing.map((c) => (
            <div key={c.id} className="text-xs text-zinc-400 border border-pb-border px-3 py-2 mb-1.5" data-testid={`callout-out-${c.id}`}>
              You called out <b className="text-white">{c.opponent_name}</b>{c.wager > 0 ? <> for <b className="text-yellow-400">{c.wager} BLC</b> (escrowed)</> : ""} — expires in 7 days
            </div>
          ))}
        </div>
      )}

      {/* Recent */}
      {recent.length > 0 && (
        <div data-testid="callouts-recent">
          <div className="text-[8px] font-black uppercase tracking-[0.3em] text-zinc-500 mb-1.5">Recent callouts</div>
          {recent.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-2 text-[11px] text-zinc-400 border-b border-dotted border-zinc-800 py-1" data-testid={`callout-recent-${c.id}`}>
              <span>
                {c.status === "declined"
                  ? <><b>{c.opponent_name}</b> declined <b>{c.challenger_name}</b>'s callout</>
                  : <><b className="text-yellow-400">{c.result?.winner_name}</b> won {c.result?.winner_id === c.challenger_id ? `${c.result?.s1}–${c.result?.s2}` : `${c.result?.s2}–${c.result?.s1}`} vs {c.result?.winner_id === c.challenger_id ? c.opponent_name : c.challenger_name}{c.result?.payout > 0 ? <> · took <b className="text-yellow-400">{c.result.payout} BLC</b></> : ""}</>}
              </span>
              {c.status === "resolved" && (
                <button data-testid={`callout-rematch-${c.id}`} onClick={() => rematch(c)} disabled={busy}
                  className="ml-auto shrink-0 flex items-center gap-1 border border-yellow-400/50 text-yellow-400 text-[8px] font-black uppercase tracking-widest px-2 py-1 hover:bg-yellow-400 hover:text-black transition-colors disabled:opacity-40"
                  title="Rematch with the wager doubled">
                  🔁 Run It Back{c.wager > 0 ? ` — ${Math.min(10000, c.wager * 2)} BLC` : ""}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {incoming.length === 0 && outgoing.length === 0 && recent.length === 0 && (
        <p className="text-xs text-zinc-600">No callouts yet — pick a rival and put some BLC where your paddle is.</p>
      )}
    </div>
  );
}
